/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";

export const REVOKE_CREDENTIAL_DISCRIMINATOR = new Uint8Array([
  38, 123, 95, 95, 223, 158, 169, 87,
]);

export function getRevokeCredentialDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    REVOKE_CREDENTIAL_DISCRIMINATOR,
  );
}

export type RevokeCredentialInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountCredential extends string | AccountMeta<string> = string,
  TAccountIssuer extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountCredential extends string
        ? WritableAccount<TAccountCredential>
        : TAccountCredential,
      TAccountIssuer extends string
        ? WritableSignerAccount<TAccountIssuer> &
            AccountSignerMeta<TAccountIssuer>
        : TAccountIssuer,
      ...TRemainingAccounts,
    ]
  >;

export type RevokeCredentialInstructionData = {
  discriminator: ReadonlyUint8Array;
};

export type RevokeCredentialInstructionDataArgs = {};

export function getRevokeCredentialInstructionDataEncoder(): FixedSizeEncoder<RevokeCredentialInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({ ...value, discriminator: REVOKE_CREDENTIAL_DISCRIMINATOR }),
  );
}

export function getRevokeCredentialInstructionDataDecoder(): FixedSizeDecoder<RevokeCredentialInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getRevokeCredentialInstructionDataCodec(): FixedSizeCodec<
  RevokeCredentialInstructionDataArgs,
  RevokeCredentialInstructionData
> {
  return combineCodec(
    getRevokeCredentialInstructionDataEncoder(),
    getRevokeCredentialInstructionDataDecoder(),
  );
}

export type RevokeCredentialInput<
  TAccountCredential extends string = string,
  TAccountIssuer extends string = string,
> = {
  credential: Address<TAccountCredential>;
  issuer: TransactionSigner<TAccountIssuer>;
};

export function getRevokeCredentialInstruction<
  TAccountCredential extends string,
  TAccountIssuer extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: RevokeCredentialInput<TAccountCredential, TAccountIssuer>,
  config?: { programAddress?: TProgramAddress },
): RevokeCredentialInstruction<
  TProgramAddress,
  TAccountCredential,
  TAccountIssuer
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    credential: { value: input.credential ?? null, isWritable: true },
    issuer: { value: input.issuer ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.credential),
      getAccountMeta(accounts.issuer),
    ],
    data: getRevokeCredentialInstructionDataEncoder().encode({}),
    programAddress,
  } as RevokeCredentialInstruction<
    TProgramAddress,
    TAccountCredential,
    TAccountIssuer
  >);
}

export type ParsedRevokeCredentialInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    credential: TAccountMetas[0];
    issuer: TAccountMetas[1];
  };
  data: RevokeCredentialInstructionData;
};

export function parseRevokeCredentialInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedRevokeCredentialInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { credential: getNextAccount(), issuer: getNextAccount() },
    data: getRevokeCredentialInstructionDataDecoder().decode(instruction.data),
  };
}
